import React, { useState, useEffect } from 'react';
import axios from '../services/axiosConfig';
import HistorialClinico from './HistorialClinico';
import PerfilPaciente from './PerfilPaciente';
import HistorialService from '../services/historiales';
import ConfirmDialog from './ConfirmDialog';
import { exportPacientes } from '../utils/exportUtils';

function Pacientes({ mostrarForm = false, onCerrarForm }) {
  const [pacientes, setPacientes] = useState([]);
  const [mostrarFormLocal, setMostrarFormLocal] = useState(mostrarForm);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [pacienteAEliminar, setPacienteAEliminar] = useState(null);
  const [mostrarConfirmEliminar, setMostrarConfirmEliminar] = useState(false);
  const [pacienteEditando, setPacienteEditando] = useState(null);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [mostrarPerfilModal, setMostrarPerfilModal] = useState(false);
  const [pacientePerfilId, setPacientePerfilId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha_nacimiento: '',
    notas: '',
    activo: true
  });

  // Función para formatear fecha para input type="date"
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // Si la fecha ya está en formato YYYY-MM-DD, devolverla tal cual
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // Convertir a fecha y formatear a YYYY-MM-DD
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    return date.toISOString().split('T')[0];
  };

  // Función para mostrar fecha en formato legible
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Agregar hora para evitar problemas de zona horaria
    return date.toLocaleDateString('es-ES');
  };

  const cargarPacientes = async () => {
    try {
      const response = await axios.get('/pacientes');
      // Manejar respuesta con paginación o array directo
      const data = response.data;
      if (Array.isArray(data)) {
        setPacientes(data);
      } else if (data && Array.isArray(data.data)) {
        setPacientes(data.data);
      } else {
        setPacientes([]);
      }
    } catch (error) {
      console.error('Error cargando pacientes:', error);
      setPacientes([]);
    }
  };

  useEffect(() => {
    cargarPacientes();
    setMostrarFormLocal(mostrarForm);
  }, [mostrarForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Asegurarnos de que la fecha esté en formato correcto
      const datosEnviar = {
        ...formData,
        fecha_nacimiento: formData.fecha_nacimiento || null
      };

      if (modoEdicion && pacienteEditando) {
        await axios.put(`/pacientes/${pacienteEditando.id}`, datosEnviar);
      } else {
        await axios.post('/pacientes', datosEnviar);
      }
      
      cerrarModal();
      cargarPacientes();
    } catch (error) {
      console.error('Error guardando paciente:', error);
      alert('Error al guardar el paciente: ' + (error.response?.data?.error || error.message));
    }
  };

  const editarPaciente = (paciente) => {
    setModoEdicion(true);
    setPacienteEditando(paciente);
    setFormData({
      nombre: paciente.nombre,
      email: paciente.email || '',
      telefono: paciente.telefono || '',
      fecha_nacimiento: formatDateForInput(paciente.fecha_nacimiento),
      notas: paciente.notas || '',
      activo: paciente.activo
    });
    setMostrarFormLocal(true);
  };

  const eliminarPaciente = (paciente) => {
    setPacienteAEliminar(paciente);
    setMostrarConfirmEliminar(true);
  };

  const confirmarEliminar = async () => {
    try {
      await axios.delete(`/pacientes/${pacienteAEliminar.id}`);
      cargarPacientes();
      setMostrarConfirmEliminar(false);
      setPacienteAEliminar(null);
    } catch (error) {
      console.error('Error eliminando paciente:', error);
      const errorMessage = error.response?.data?.error || 'Error al eliminar el paciente';
      alert(errorMessage);
    }
  };

  const cancelarEliminar = () => {
    setMostrarConfirmEliminar(false);
    setPacienteAEliminar(null);
  };

  const generarLinkPaciente = async (paciente) => {
    try {
      const response = await axios.post(`/pacientes/${paciente.id}/generar-token`);
      const link = response.data.link;
      
      // Copiar al portapapeles
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        alert(`✅ Link copiado al portapapeles!\n\n${link}\n\nEnvía este link al paciente ${paciente.nombre} para que pueda agendar sus turnos.`);
      } else {
        // Fallback para navegadores que no soportan clipboard API
        prompt('Copia este link y envíaselo al paciente:', link);
      }
    } catch (error) {
      console.error('Error generando link:', error);
      alert('Error al generar el link: ' + (error.response?.data?.error || error.message));
    }
  };

  const nuevoPaciente = () => {
    setModoEdicion(false);
    setPacienteEditando(null);
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      fecha_nacimiento: '',
      notas: '',
      activo: true
    });
    setMostrarFormLocal(true);
  };

  const cerrarModal = () => {
    setMostrarFormLocal(false);
    setModoEdicion(false);
    setPacienteEditando(null);
    if (onCerrarForm) onCerrarForm();
  };

  const toggleEstadoPaciente = async (paciente) => {
    try {
      await axios.put(`/pacientes/${paciente.id}`, {
        ...paciente,
        activo: !paciente.activo
      });
      cargarPacientes();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      alert('Error al cambiar el estado del paciente');
    }
  };

  const verHistorial = async (paciente) => {
    try {
      // Crear historial si no existe
      await HistorialService.crearHistorial(paciente.id).catch(() => {
        // El historial ya existe, ignorar error
      });
      setPacienteSeleccionado(paciente);
      setMostrarHistorial(true);
    } catch (error) {
      console.error('Error al abrir historial:', error);
      alert('Error al abrir el historial clínico');
    }
  };

  const cerrarHistorial = () => {
    setMostrarHistorial(false);
    setPacienteSeleccionado(null);
  };

  return (
    <div className="pacientes-page">
      <div className="page-header">
        <h2>Gestión de Pacientes</h2>
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={() => exportPacientes(pacientes, 'excel')}
            title="Exportar a Excel"
          >
            📊 Excel
          </button>
          <button 
            className="btn-secondary"
            onClick={() => exportPacientes(pacientes, 'pdf')}
            title="Exportar a PDF"
          >
            📄 PDF
          </button>
          <button 
            className="btn-primary"
            onClick={nuevoPaciente}
          >
            ➕ Nuevo Paciente
          </button>
        </div>
      </div>

      {mostrarFormLocal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{modoEdicion ? 'Editar Paciente' : 'Nuevo Paciente'}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Nombre completo *"
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
              <input
                type="tel"
                placeholder="Teléfono"
                value={formData.telefono}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              />
              <input
                type="date"
                placeholder="Fecha de nacimiento"
                value={formData.fecha_nacimiento}
                onChange={(e) => setFormData({...formData, fecha_nacimiento: e.target.value})}
              />
              <small style={{color: '#666', fontSize: '12px'}}>
                Formato: AAAA-MM-DD (ej: 1972-04-09)
              </small>
              <textarea
                placeholder="Notas adicionales"
                value={formData.notas}
                onChange={(e) => setFormData({...formData, notas: e.target.value})}
                rows="3"
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.activo}
                  onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                />
                Paciente activo
              </label>
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {modoEdicion ? 'Actualizar' : 'Guardar'}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={cerrarModal}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="pacientes-list">
        {pacientes.map(paciente => (
          <div key={paciente.id} className="paciente-card">
            <div className="paciente-info">
              <div className="paciente-header">
                <h3>{paciente.nombre}</h3>
                <div className="paciente-estado">
                  <span className={`estado ${paciente.activo ? 'activo' : 'inactivo'}`}>
                    {paciente.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
              
              <p>📧 {paciente.email || 'Sin email'}</p>
              <p>📞 {paciente.telefono || 'Sin teléfono'}</p>
              {paciente.fecha_nacimiento && (
                <p>🎂 {formatDisplayDate(paciente.fecha_nacimiento)}</p>
              )}
              {paciente.notas && <p>📝 {paciente.notas}</p>}
              
              <div className="paciente-actions">
                <button 
                  className="btn-small btn-edit"
                  onClick={() => editarPaciente(paciente)}
                  title="Editar paciente"
                >
                  ✏️ Editar
                </button>
                <button 
                  className="btn-small btn-profile"
                  onClick={() => {
                    setPacientePerfilId(paciente.id);
                    setMostrarPerfilModal(true);
                  }}
                  title="Ver perfil mejorado"
                >
                  👤 Perfil
                </button>
                <button 
                  className="btn-small btn-history"
                  onClick={() => verHistorial(paciente)}
                  title="Ver historial clínico"
                >
                  📋 Historial
                </button>
                <button 
                  className="btn-small btn-link"
                  onClick={() => generarLinkPaciente(paciente)}
                  title="Generar link para agendar turnos"
                >
                  🔗 Link Agenda
                </button>
                <button 
                  className="btn-small btn-toggle"
                  onClick={() => toggleEstadoPaciente(paciente)}
                  title={paciente.activo ? 'Desactivar paciente' : 'Activar paciente'}
                >
                  {paciente.activo ? '❌ Desactivar' : '✅ Activar'}
                </button>
                <button 
                  className="btn-small btn-delete"
                  onClick={() => eliminarPaciente(paciente)}
                  title="Eliminar paciente"
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pacientes.length === 0 && (
        <div className="empty-state">
          <p>No hay pacientes registrados todavía.</p>
          <button className="btn-primary" onClick={nuevoPaciente}>
            ➕ Agregar primer paciente
          </button>
        </div>
      )}

      {mostrarHistorial && pacienteSeleccionado && (
        <div className="modal-overlay" onClick={cerrarHistorial}>
          <div className="modal-content historial-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Historial Clínico</h2>
              <button 
                className="btn-close" 
                onClick={cerrarHistorial}
                title="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <HistorialClinico 
                paciente_id={pacienteSeleccionado.id}
                paciente_nombre={pacienteSeleccionado.nombre}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Perfil Paciente */}
      {mostrarPerfilModal && (
        <div className="modal-overlay" onClick={() => setMostrarPerfilModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <PerfilPaciente
              paciente_id={pacientePerfilId}
              onClose={() => setMostrarPerfilModal(false)}
            />
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmDialog
        isOpen={mostrarConfirmEliminar}
        title="Eliminar Paciente"
        message={`¿Estás seguro de que deseas eliminar al paciente "${pacienteAEliminar?.nombre}"?`}
        detail="Esta acción no se puede deshacer. Se eliminarán todos los datos asociados (citas, historial, pagos)."
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        onConfirm={confirmarEliminar}
        onCancel={cancelarEliminar}
        isDangerous={true}
      />
    </div>
  );
}

export default Pacientes;
